import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["totalAmount", "paymentAmount", "balanceAfter"]
  static values = {
    balanceBeforeCents: Number,
    refundableByPaymentId: Object,
    paymentOrder: Array,
    defaultTotalCents: Number,
    maxTotalCents: Number
  }

  connect() {
    this.distributing = false
    this.prefillDefaultTotal()
    this.updateBalance()
  }

  prepareSubmit(event) {
    this.clampAllPaymentAmounts()
    this.formatTotalAmount()
    this.syncTotalFromPayments({ format: true })

    const totalCents = this.currentTotalCents()
    if (totalCents <= 0) {
      event.preventDefault()
    }
  }

  onTotalInput() {
    this.updateBalance()
  }

  onTotalBlur() {
    this.formatTotalAmount()
    this.distributeFromTotal()
  }

  onPaymentAmountInput() {
    this.syncTotalFromPayments({ format: false })
    this.updateBalance()
  }

  onPaymentAmountBlur(event) {
    this.clampPaymentAmount(event.target, { format: true })
    this.syncTotalFromPayments({ format: true })
    this.updateBalance()
  }

  distributeFromTotal() {
    if (this.distributing) return

    let totalCents = this.parseAmountCents(this.totalAmountTarget)
    totalCents = Math.min(totalCents, this.maxTotalCentsValue)
    const allocations = this.lifoAllocations(totalCents)

    this.distributing = true
    this.paymentAmountTargets.forEach((input) => {
      const paymentId = input.dataset.paymentId
      const cents = allocations[paymentId] || 0
      input.value = cents > 0 ? this.formatAmountInput(cents) : ""
    })
    this.distributing = false
    this.syncTotalFromPayments({ format: true })
    this.updateBalance()
  }

  syncTotalFromPayments({ format = false } = {}) {
    if (this.distributing) return

    const totalCents = this.currentTotalCents()

    if (totalCents <= 0) {
      this.totalAmountTarget.value = ""
      return
    }

    if (format) {
      this.totalAmountTarget.value = this.formatAmountInput(totalCents)
      return
    }

    this.totalAmountTarget.value = this.formatAmountDraft(totalCents)
  }

  clampAllPaymentAmounts() {
    this.paymentAmountTargets.forEach((input) => this.clampPaymentAmount(input, { format: true }))
  }

  clampPaymentAmount(input, { format = false } = {}) {
    const maxCents = Number.parseInt(input.dataset.maxCents, 10) || 0
    const cents = this.parseAmountCents(input)

    if (cents <= maxCents) {
      if (format && cents > 0) {
        input.value = this.formatAmountInput(cents)
      }
      return
    }

    input.value = maxCents > 0 ? this.formatAmountInput(maxCents) : ""
  }

  formatTotalAmount() {
    const cents = this.parseAmountCents(this.totalAmountTarget)
    if (cents <= 0) {
      this.totalAmountTarget.value = ""
      return
    }

    this.totalAmountTarget.value = this.formatAmountInput(cents)
  }

  prefillDefaultTotal() {
    if (this.defaultTotalCentsValue <= 0) return
    if (this.totalAmountTarget.value.trim() !== "") return

    this.totalAmountTarget.value = this.formatAmountInput(this.defaultTotalCentsValue)
    this.distributeFromTotal()
  }

  currentTotalCents() {
    return this.paymentAmountTargets.reduce((sum, input) => {
      return sum + this.parseAmountCents(input)
    }, 0)
  }

  lifoAllocations(totalCents) {
    const remainingByPayment = this.refundableByPaymentIdValue
    const orderedPaymentIds = this.paymentOrderValue.length > 0
      ? this.paymentOrderValue
      : Object.keys(remainingByPayment)
    const allocations = {}
    let remaining = totalCents

    orderedPaymentIds.forEach((paymentId) => {
      if (remaining <= 0) return

      const maxCents = Number.parseInt(remainingByPayment[paymentId], 10) || 0
      const amountCents = Math.min(remaining, maxCents)
      if (amountCents > 0) {
        allocations[paymentId] = amountCents
        remaining -= amountCents
      }
    })

    return allocations
  }

  updateBalance() {
    const refundCents = this.totalAmountTarget.value.trim() === ""
      ? this.currentTotalCents()
      : this.parseAmountCents(this.totalAmountTarget)
    const afterCents = this.balanceBeforeCentsValue + refundCents
    this.balanceAfterTarget.textContent = this.formatCustomerBalance(afterCents)
  }

  parseAmountCents(input) {
    const raw = input.value.trim().replace(/[€\s\u00A0]/g, "").replace(",", ".")
    if (raw === "") return 0

    const value = Number.parseFloat(raw)
    if (Number.isNaN(value) || value === 0) return 0

    return Math.round(value * 100)
  }

  formatAmountInput(cents) {
    return (cents / 100).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  formatAmountDraft(cents) {
    const euros = cents / 100
    return String(euros).replace(".", ",")
  }

  formatCustomerBalance(balanceCents) {
    const customerCents = balanceCents === 0 ? 0 : -balanceCents

    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR"
    }).format(customerCents / 100)
  }
}
